import {
  FolderPlus, Inbox, Folder, Pencil, Trash2, Check, X,
} from "lucide-react";
import FolderTile from "./FolderTile";

/**
 * Sidebar with the Uncategorized bucket pinned + user folders.
 */
export default function FolderSidebar({
  folders, uncatCount, activeFolder, setActiveFolder,
  showNewFolder, setShowNewFolder, newFolderName, setNewFolderName, onCreateFolder,
  renamingId, renameValue, setRenameValue,
  onStartRename, onCommitRename, onCancelRename, onDeleteFolder,
}) {
  return (
    <aside className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-3 h-fit lg:sticky lg:top-6" data-testid="catalog-folder-sidebar">
      <div className="flex items-center justify-between px-2 pt-1 pb-2">
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">Folders</span>
        <button
          onClick={() => setShowNewFolder((v) => !v)}
          data-testid="catalog-new-folder-btn"
          className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-white/15 text-white/65 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition"
          title="New folder"
        >
          <FolderPlus className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Uncategorized */}
      <FolderTile
        active={activeFolder === "__uncat__"}
        onClick={() => setActiveFolder("__uncat__")}
        icon={<Inbox className="w-3.5 h-3.5" />}
        label="Uncategorized"
        count={uncatCount}
        testId="catalog-folder-uncat"
      />

      <div className="h-px bg-white/5 my-2" />

      {/* User folders */}
      <ul className="space-y-1">
        {folders.map((f) => {
          const isRename = renamingId === f.id;
          if (isRename) {
            return (
              <li key={f.id} data-testid={`catalog-folder-${f.id}-renaming`}>
                <div className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-[#141414] border border-[#D4AF37]/40">
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onCommitRename(f);
                      if (e.key === "Escape") onCancelRename();
                    }}
                    maxLength={60}
                    className="flex-1 bg-transparent text-sm text-white outline-none"
                    data-testid={`catalog-folder-${f.id}-rename-input`}
                  />
                  <button
                    onClick={() => onCommitRename(f)}
                    className="text-[#D4AF37] hover:text-[#F3CE5A]"
                    data-testid={`catalog-folder-${f.id}-rename-save`}
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={onCancelRename} className="text-white/45 hover:text-white">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            );
          }
          return (
            <li key={f.id} className="group/folder">
              <FolderTile
                active={activeFolder === f.id}
                onClick={() => setActiveFolder(f.id)}
                icon={<Folder className="w-3.5 h-3.5" />}
                label={f.name}
                count={f.item_count}
                testId={`catalog-folder-${f.id}`}
                actions={
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); onStartRename(f); }}
                      className="text-white/45 hover:text-[#D4AF37]"
                      data-testid={`catalog-folder-${f.id}-rename-btn`}
                      title="Rename"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onDeleteFolder(f); }}
                      className="text-white/45 hover:text-rose-300"
                      data-testid={`catalog-folder-${f.id}-delete-btn`}
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </>
                }
              />
            </li>
          );
        })}
      </ul>

      {showNewFolder && (
        <div className="mt-3 px-2 pb-1" data-testid="catalog-new-folder-form">
          <input
            autoFocus
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onCreateFolder();
              if (e.key === "Escape") { setShowNewFolder(false); setNewFolderName(""); }
            }}
            maxLength={60}
            placeholder="Bridal AW26"
            className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37]"
            data-testid="catalog-new-folder-input"
          />
          <div className="mt-1.5 flex gap-1.5">
            <button
              onClick={onCreateFolder}
              className="flex-1 text-xs rounded-md bg-[#D4AF37] text-black py-1.5 hover:bg-[#F3CE5A]"
              data-testid="catalog-new-folder-create"
            >
              Create
            </button>
            <button
              onClick={() => { setShowNewFolder(false); setNewFolderName(""); }}
              className="text-xs rounded-md border border-white/10 text-white/55 px-3 py-1.5 hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {folders.length === 0 && !showNewFolder && (
        <p className="text-[11px] text-white/35 px-2 mt-3">
          No folders yet. Group catalogue shoots by collection so you can ZIP-download them in one click.
        </p>
      )}
    </aside>
  );
}
